<template>
  <UPageHero
    class="block-landing-hero"
    :ui="{
      container: 'pt-6 sm:pt-8 lg:pt-10 pb-16 sm:pb-20 lg:pb-24',
      headline: 'mb-8 flex w-full items-center justify-center sm:mb-10',
      title: 'text-shadow-md',
      links: 'mt-4 flex-col justify-center items-center',
      description: 'text-pretty',
    }"
  >
    <template v-if="slides.length" #headline>
      <div class="block-landing-hero__slider relative mx-auto w-full max-w-5xl">
        <ClientOnly v-if="slides.length > 1">
          <button
            type="button"
            :class="[prevClass, 'block-landing-hero__nav block-landing-hero__nav--prev']"
            :aria-label="prevLabel"
          >
            <UIcon :name="dir === 'rtl' ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'" class="size-5" />
          </button>
          <button
            type="button"
            :class="[nextClass, 'block-landing-hero__nav block-landing-hero__nav--next']"
            :aria-label="nextLabel"
          >
            <UIcon :name="dir === 'rtl' ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right'" class="size-5" />
          </button>
          <Swiper
            :key="swiperKey"
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="1"
            :space-between="0"
            :loop="canLoop"
            :autoplay="autoplayOptions"
            :navigation="navigationOptions"
            :pagination="paginationOptions"
            :auto-height="true"
            observer
            observe-parents
            class="block-landing-hero-swiper w-full"
          >
            <SwiperSlide
              v-for="(src, index) in slides"
              :key="`${index}-${src.length}`"
              class="!flex items-center justify-center"
            >
              <div class="block-landing-hero__frame">
                <img
                  :src="src"
                  :alt="title || 'Hero'"
                  class="block-landing-hero__media"
                >
              </div>
            </SwiperSlide>
          </Swiper>
          <template #fallback>
            <div class="flex justify-center">
              <div class="block-landing-hero__frame">
                <img
                  :src="slides[0]"
                  :alt="title || 'Hero'"
                  class="block-landing-hero__media"
                >
              </div>
            </div>
          </template>
        </ClientOnly>
        <div v-else class="flex justify-center">
          <div class="block-landing-hero__frame">
            <img
              :src="slides[0]"
              :alt="title || 'Hero'"
              class="block-landing-hero__media"
            >
          </div>
        </div>
      </div>
    </template>

    <template v-if="hasTitle" #title>
      <div :dir="dir">
        {{ title }}
      </div>
    </template>

    <template v-if="hasDescription" #description>
      <div :dir="dir">
        {{ description }}
      </div>
    </template>

    <template #links>
      <div class="flex flex-wrap items-center justify-center gap-2">
        <UButton
          v-if="primaryButton?.label && builderPreview"
          :label="primaryButton.label"
          size="xl"
          color="primary"
          class="block-landing-hero__cta"
          @click="navigate(primaryButton.to || '#')"
        />
        <UButton
          v-else-if="primaryButton?.label"
          :label="primaryButton.label"
          size="xl"
          :to="primaryButton.to || '#'"
          color="primary"
          class="block-landing-hero__cta"
        />
        <UButton
          v-for="(link, index) in normalizedSecondaryLinks"
          :key="index"
          v-bind="{ size: 'xl', color: 'neutral', variant: 'ghost', ...link }"
          :to="builderPreview ? undefined : (link.to || link.href || undefined)"
          class="block-landing-hero__cta"
          @click="builderPreview && (link.to || link.href) ? navigate(String(link.to || link.href)) : undefined"
        />
        <template v-if="displaySocialLinks.length">
          <a
            v-for="(link, i) in displaySocialLinks"
            :key="'social-' + i"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            :aria-label="link.url"
          >
            <UIcon :name="link.icon" class="size-6" />
          </a>
        </template>
      </div>
    </template>
  </UPageHero>
</template>

<script setup lang="ts">
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')
const navScope = `landing-hero-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
const prevClass = `${navScope}-prev`
const nextClass = `${navScope}-next`
const swiperModules = [Pagination, Navigation, Autoplay]

const props = withDefaults(defineProps<{
  /** @deprecated Prefer headlineImages */
  headlineImage?: string | string[]
  headlineImages?: string | string[]
  /** Advance to the next banner after `bannerAutoplayDelay`. */
  bannerAutoplay?: boolean
  /** Delay in milliseconds between banners. */
  bannerAutoplayDelay?: number
  /** After the last banner, the next action returns to the first. */
  bannerLoop?: boolean
  title?: string
  description?: string
  primaryButton?: { label?: string; to?: string }
  secondaryLinks?: Array<{ label?: string; to?: string; href?: string; [key: string]: unknown }>
  socialLinks?: Array<{ icon: string; url: string }>
  builderPreview?: boolean
  onNavigate?: (to: string) => void
}>(), {
  bannerAutoplay: false,
  bannerAutoplayDelay: 5000,
  bannerLoop: false,
})

function toSlideList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((src): src is string => typeof src === 'string' && Boolean(src.trim()))
  }
  if (typeof value === 'string' && value.trim()) return [value]
  return []
}

const slides = computed(() => {
  const fromList = toSlideList(props.headlineImages)
  if (fromList.length) return fromList
  return toSlideList(props.headlineImage)
})

const autoplayDelay = computed(() => {
  const value = Number(props.bannerAutoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 5000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => props.bannerLoop && slides.value.length > 1)

const autoplayOptions = computed(() => {
  if (!props.bannerAutoplay || slides.value.length < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.bannerLoop,
  }
})

const navigationOptions = computed(() => ({
  prevEl: `.${prevClass}`,
  nextEl: `.${nextClass}`,
}))

const paginationOptions = computed(() => ({
  clickable: true,
}))

const swiperKey = computed(() =>
  [
    dir.value,
    slides.value.length,
    slides.value.map(src => src.length).join(','),
    props.bannerAutoplay ? '1' : '0',
    props.bannerLoop ? '1' : '0',
    autoplayDelay.value,
  ].join(':'),
)

const prevLabel = computed(() => (pageLocale.value === 'fa' ? 'بنر قبلی' : 'Previous banner'))
const nextLabel = computed(() => (pageLocale.value === 'fa' ? 'بنر بعدی' : 'Next banner'))

const hasTitle = computed(() => Boolean(props.title?.trim()))
const hasDescription = computed(() => Boolean(props.description?.trim()))
const displaySocialLinks = computed(() => (props.socialLinks ?? []).slice(0, 2))

const normalizedSecondaryLinks = computed(() =>
  (props.secondaryLinks ?? []).filter(
    (link): link is { label?: string; to?: string; href?: string; [key: string]: unknown } =>
      Boolean(link) && typeof link === 'object',
  ),
)

function navigate(to: string) {
  if (props.builderPreview && props.onNavigate) {
    props.onNavigate(to)
  }
}
</script>

<style scoped>
.block-landing-hero__frame {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  border-radius: 1rem;
  line-height: 0;
}

.block-landing-hero__media {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: min(70vh, 560px);
  object-fit: contain;
}

.block-landing-hero__nav {
  position: absolute;
  top: calc(50% - 0.75rem);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, var(--ui-text) 18%, transparent);
  background: color-mix(in srgb, var(--ui-bg) 82%, transparent);
  color: var(--ui-text);
  cursor: pointer;
  transform: translateY(-50%);
}

.block-landing-hero__nav--prev {
  inset-inline-start: 0.75rem;
}

.block-landing-hero__nav--next {
  inset-inline-end: 0.75rem;
}

.block-landing-hero__nav.swiper-button-disabled {
  opacity: 0.35;
  cursor: default;
}

.block-landing-hero-swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 0.75rem;
}

.block-landing-hero-swiper :deep(.swiper-pagination-bullet-active) {
  background: var(--ui-primary);
}

.block-landing-hero__media {
  animation: hero-fade-in 0.6s ease-out both;
}

.block-landing-hero__cta {
  animation: hero-fade-in-cta 0.6s ease-out both;
}

.block-landing-hero__cta:nth-child(2) {
  animation-delay: 0.1s;
}

.block-landing-hero__cta:nth-child(3) {
  animation-delay: 0.2s;
}

@keyframes hero-fade-in {
  from {
    opacity: 0;
    filter: blur(12px);
  }
  to {
    opacity: 1;
    filter: blur(0);
  }
}

@keyframes hero-fade-in-cta {
  from {
    opacity: 0;
    transform: scale(1.05);
    filter: blur(12px);
  }
  to {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .block-landing-hero__media,
  .block-landing-hero__cta {
    animation: none;
  }
}
</style>
